import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Close } from '@mui/icons-material';
import { Box, Button, darken, Divider, Grid, IconButton, MenuItem, Popover, rgbToHex, Tooltip, useTheme, } from '@mui/material';
import Select from '../Select';
import TextField from '../TextField';
import { Subheader3 } from '../Typography';
import { usePopoverState } from '../../hooks';
import { PickerContainer } from './ColorPicker.styles';
const defaultColors = [
    '#ED8C8C',
    '#FFC857',
    '#4096FF',
    '#9747FF',
    '#28A745',
    '#9DBBE7',
    '#E7EFF9',
    '#FF4D4F',
    '#C2C3C3',
    '#3C3C3C',
    '#000000',
    '#0A56C2',
    '#DC3545',
    '#D4EDDA',
    '#CEDDF3',
];
const Mode = {
    HEX: 'HEX',
    RGB: 'RGB',
};
const hexToRgbNumbers = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) {
        return null;
    }
    return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`;
};
const ColorPicker = ({ title, color: initialColor = '#FFFFFF', colors = defaultColors, onChange, }) => {
    const theme = useTheme();
    const rgbInputRef = useRef(null);
    const hexInputRef = useRef(null);
    const [anchorRef, open, onOpen, onClose] = usePopoverState();
    const [mode, setMode] = useState(Mode.HEX);
    const initialHexColor = useMemo(() => {
        let hexColor = initialColor;
        if (!initialColor.startsWith('#')) {
            hexColor = rgbToHex(initialColor).substring(0, 7);
        }
        return hexColor.toUpperCase();
    }, []);
    const [color, setColor] = useState(initialHexColor);
    const onChangeColor = (hexColor) => {
        hexColor = hexColor.toUpperCase();
        setColor(hexColor);
        if (hexInputRef.current) {
            hexInputRef.current.value = hexColor;
        }
        if (rgbInputRef.current) {
            const rgbNumbers = hexToRgbNumbers(hexColor);
            if (rgbNumbers) {
                rgbInputRef.current.value = rgbNumbers;
            }
        }
        onChange(hexColor);
    };
    const onChangeHexColor = (e) => {
        const color = e.target.value || '';
        const isValid = /^#[0-9A-F]{6}$/i.test(color);
        if (isValid) {
            onChangeColor(color);
        }
    };
    const onChangeRgbColor = (e) => {
        const color = e.target.value || '';
        const isValid = /^(\d{1,3})\s(\d{1,3})\s(\d{1,3})$/i.test(color);
        if (isValid) {
            const [r, g, b] = color.split(' ').map((c) => parseInt(c, 10));
            onChangeColor(`#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`);
        }
    };
    const rgbColor = `${parseInt(color.slice(1, 3), 16)} ${parseInt(color.slice(3, 5), 16)} ${parseInt(color.slice(5, 7), 16)}`;
    return (_jsxs(_Fragment, { children: [_jsx(Box, { ref: anchorRef, children: _jsx(Button, { component: 'button', sx: {
                        cursor: 'pointer',
                        border: `1px solid ${darken(color, 0.2)}`,
                        borderRadius: 0.5,
                        backgroundColor: color,
                        height: 32,
                        width: 32,
                        padding: 0,
                        transition: theme.transitions.create('background-color'),
                        '&:hover': {
                            backgroundColor: darken(color, 0.1),
                        },
                    }, onClick: onOpen }) }), _jsx(Popover, { open: open, onClose: onClose, anchorEl: anchorRef.current, anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                }, transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                }, children: _jsxs(Box, { display: 'display', flexDirection: 'column', overflow: 'hidden', width: 250, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0, p: 1, children: [_jsx(Subheader3, { noWrap: true, ml: 1, children: title }), _jsx(Tooltip, { title: 'Close', children: _jsx(IconButton, { size: 'small', onClick: onClose, children: _jsx(Close, { fontSize: 'medium' }) }) })] }), _jsx(PickerContainer, { children: _jsx(HexColorPicker, { className: 'uikit-color-picker', color: color, onChange: onChangeColor }) }), _jsxs(Grid, { container: true, spacing: 1, p: 2, pt: 0, children: [_jsx(Grid, { item: true, xs: 5, children: _jsxs(Select, { value: mode, onChange: (e) => setMode(e.target.value), children: [_jsx(MenuItem, { value: Mode.HEX, children: "HEX" }), _jsx(MenuItem, { value: Mode.RGB, children: "RGB" })] }) }), mode == Mode.RGB && (_jsx(Grid, { item: true, xs: 7, children: _jsx(TextField, { inputRef: rgbInputRef, defaultValue: rgbColor, fullWidth: true, onChange: onChangeRgbColor }) })), mode == Mode.HEX && (_jsx(Grid, { item: true, xs: 7, children: _jsx(TextField, { inputRef: hexInputRef, defaultValue: color, fullWidth: true, onChange: onChangeHexColor }) }))] }), _jsx(Divider, {}), _jsx(Box, { p: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75, children: colors.map((color) => (_jsx(Box, { border: `1px solid ${darken(color, 0.05)}`, sx: {
                                    cursor: 'pointer',
                                    backgroundColor: color,
                                }, borderRadius: 0.25, height: 22, width: 22, onClick: () => onChangeColor(color) }, color))) })] }) })] }));
};
export default ColorPicker;
